import * as actionTypes from './actionTypes'
// Constants
import { platforms } from '../../constants'

/**
 * Helper function for funneling all notification types into a single action.
 * @method notificationHelper
 * @param  {string} channel - The channel that the notification came from.
 * @param  {Object} notification
 * @param  {string} platform
 * @return {Object} A flux standard action.
 */
function notificationHelper (channel, notification, platform) {
  return {
    type: actionTypes.PROCESS_NOTIFICATION,
    payload: notification,
    meta: {
      platform,
      channel
    }
  }
}

/**
 * Represents an application request to process a websocket notification.
 * @method websocketNotification
 * @param  {Object} notification
 * @param  {string} platform
 * @return {Object} A flux standard action.
 */
export function websocketNotification (notification, platform = platforms.LINK) {
  return notificationHelper('WEBSOCKET', notification, platform)
}

/**
 * Represents an application request to process an external notification.
 * @method externalNotification
 * @param  {Object} notification
 * @param  {string} [channel='PUSH'] - The channel that the notification came from.
 * @return {Object} A flux standard action.
 */
export function externalNotification (notification, channel = 'PUSH', platform) {
  // TODO: Are external notifications _only_ for Link?
  return notificationHelper(channel.toUpperCase(), notification, platform)
}

/**
 * Represents a received notification.
 * @method notificationReceived
 * @param  {Object} notification
 * @param  {string} platform
 * @return {Object} A flux standard action.
 */
export function notificationReceived (notification, platform, channel) {
  return {
    type: actionTypes.NOTIFICATION_RECEIVED,
    payload: notification,
    error: notification instanceof Error,
    meta: {
      platform,
      channel
    }
  }
}

/**
 * Represents a received notification.
 * @method processNotificationFinish
 * @param  {Object} notification
 * @param  {string} platform
 * @return {Object} A flux standard action.
 */
export function processNotificationFinish (notification, platform) {
  return {
    type: actionTypes.PROCESS_NOTIFICATION_FINISH,
    payload: notification,
    error: notification instanceof Error,
    meta: {
      platform
    }
  }
}

/**
 * Represents a request to change a notification channel status.
 * @method enableNotificationChannel
 * @param  {string} channel - The notification channel being affected.
 * @param  {Object} params
 * @param  {boolean} params.channelEnabled - Whether to enable the channel or not.
 * @return {Object} A flux standard action.
 */
export function enableNotificationChannel (channel, params = {}) {
  return {
    type: actionTypes.ENABLE_NOTIFICATION_CHANNEL,
    payload: {
      ...params
    },
    meta: {
      channel
    }
  }
}

/**
 * Represents a request to change a notification channel status.
 * @method registerApplePushNotification
 * @param  {Object} params
 * @return {Object} A flux standard action.
 */
export function registerApplePushNotification (params, deferred) {
  return {
    type: actionTypes.REGISTER_APPLE_PUSH_NOTIFICATION,
    payload: {
      ...params,
      pushProvider: 'apple'
    },
    meta: {
      deferred
    }
  }
}

/**
 * Represents a request to change a notification channel status.
 * @method registerAndroidPushNotification
 * @param  {Object} params
 * @return {Object} A flux standard action.
 */
export function registerAndroidPushNotification (params, deferred) {
  return {
    type: actionTypes.REGISTER_ANDROID_PUSH_NOTIFICATION,
    payload: {
      ...params,
      pushProvider: 'google'
    },
    meta: {
      deferred
    }
  }
}

/**
 * Represents a request to change a notification channel status.
 * @method unregisterApplePushNotification
 * @param  {string} registration
 * @return {Object} A flux standard action.
 */
export function unregisterApplePushNotification (registration, deferred) {
  return {
    type: actionTypes.UNREGISTER_APPLE_PUSH_NOTIFICATION,
    payload: {
      registration
    },
    meta: {
      deferred
    }
  }
}

/**
 * Represents a request to change a notification channel status.
 * @method unregisterAndroidPushNotification
 * @param  {string} registration
 * @return {Object} A flux standard action.
 */
export function unregisterAndroidPushNotification (registration, deferred) {
  return {
    type: actionTypes.UNREGISTER_ANDROID_PUSH_NOTIFICATION,
    payload: {
      registration
    },
    meta: {
      deferred
    }
  }
}

/**
 * Represents the response of a change in a notification channel status.
 * @method enableNotificationChannelFinish
 * @param  {string} channel - The notification channel being affected.
 * @param  {Object} $1
 * @param  {Object} $1.params - Information about the channel change.
 * @param  {Object} $1.params.channelEnabled - Whether the channel was enabled or not.
 * @param  {Object} $1.error - Error object, in the case of an error.
 * @return {Object} A flux standard action.
 */
export function enableNotificationChannelFinish (channel, { params, error } = {}) {
  var action = {
    type: actionTypes.ENABLE_NOTIFICATION_CHANNEL_FINISH,
    meta: {
      channel
    }
  }

  if (error) {
    action.error = true
    action.payload = new Error(error)
    action.payload.channelEnabled = params.channelEnabled
  } else {
    action.payload = {
      ...params
    }
  }

  return action
}
